import { Controller } from '@hotwired/stimulus'

// Manages BOLT insight cards in the financial package email compose step.
export default class extends Controller {
  static targets = [
    'card',
    'approveButton',
    'rejectButton',
    'removeButton',
    'undoButton',
    'pendingActions',
    'approvedActions',
    'rejectedActions',
    'headline',
    'content',
    'rejectForm',
    'rejectReasonInput',
    'regenerateButton',
    'retryButton',
    'insightsList',
    'counter',
    'summaryText'
  ]

  static values = {
    taskId: String,
    regenerateUrl: String,
    retryUrl: String,
    period: String
  }

  connect() {
    requestAnimationFrame(() => this._notifyComposeInsightsChanged())
  }

  // ── Public actions ────────────────────────────────────────────────────────

  approve(event) {
    const card = this._cardFor(event)
    this._updateInsight(card, 'approved')
  }

  remove(event) {
    const card = this._cardFor(event)
    this._updateInsight(card, 'pending')
  }

  undoReject(event) {
    const card = this._cardFor(event)
    this._updateInsight(card, 'pending')
  }

  showRejectForm(event) {
    const card = this._cardFor(event)
    const form = card.querySelector('[data-financial-package-insights-target="rejectForm"]')
    if (!form) return

    // Toggle: if already visible, cancel
    if (!form.classList.contains('d-none')) {
      this._hideRejectForm(card)
      return
    }

    // Hide any other open reject forms first
    this.rejectFormTargets.forEach(f => {
      if (f !== form) f.classList.add('d-none')
    })

    form.classList.remove('d-none')
    form.querySelector('textarea')?.focus()
  }

  cancelReject(event) {
    const card = this._cardFor(event)
    this._hideRejectForm(card)
  }

  selectChip(event) {
    const btn = event.currentTarget
    const isSelected = btn.dataset.selected === 'true'

    if (isSelected) {
      btn.dataset.selected = 'false'
      btn.style.background  = 'var(--bolt-chip-bg)'
      btn.style.color       = 'var(--bolt-chip-color)'
      btn.style.borderColor = 'var(--bolt-chip-border)'
    } else {
      btn.dataset.selected = 'true'
      btn.style.background  = '#212529'
      btn.style.color       = '#fff'
      btn.style.borderColor = '#212529'
    }
  }

  confirmReject(event) {
    const card        = this._cardFor(event)
    const input       = card.querySelector('[data-financial-package-insights-target="rejectReasonInput"]')
    const selectedChips = [...card.querySelectorAll('.reject-chip[data-selected="true"]')]
      .map(c => c.dataset.reason)
    const freeText    = input?.value?.trim() || ''

    const parts = [...selectedChips]
    if (freeText) parts.push(freeText)
    const reason = parts.length > 0 ? parts.join('; ') : null

    this._updateInsight(card, 'rejected', reason)
  }

  async regenerate() {
    await this._runAnalysisRequest({
      url: this.regenerateUrlValue,
      buttonTarget: this.hasRegenerateButtonTarget ? this.regenerateButtonTarget : null,
      onStart: () => {
        const approvedCards = this.cardTargets.filter(c => c.dataset.insightStatus === 'approved')
        const hasApproved = approvedCards.length > 0
        const banner = document.getElementById('bolt-analysis-banner')
        if (banner && !hasApproved) banner.innerHTML = ''
        if (hasApproved) {
          this._prepareRegenerateWithApproved()
        } else {
          this._showFullPanelSpinner()
        }
      },
      onError: (banner, hasApproved) => {
        if (banner && !hasApproved) {
          banner.innerHTML = '<p class="small text-danger mb-3">Regeneration failed. Please try again.</p>'
        }
      }
    })
  }

  async retryAnalysis() {
    await this._runAnalysisRequest({
      url: this.retryUrlValue,
      buttonTarget: this.hasRetryButtonTarget ? this.retryButtonTarget : null,
      onStart: () => {
        document.getElementById('bolt-analysis-banner')?.replaceChildren()
        this._showFullPanelSpinner('analyzing')
      },
      onError: () => {}
    })
  }

  async _runAnalysisRequest({ url, buttonTarget, onStart, onError }) {
    const approvedCards = this.cardTargets.filter(c => c.dataset.insightStatus === 'approved')
    const hasApproved = approvedCards.length > 0
    const banner = document.getElementById('bolt-analysis-banner')
    const originalButtonHtml = buttonTarget?.innerHTML

    if (buttonTarget) {
      buttonTarget.disabled = true
      buttonTarget.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" style="width: 0.75em; height: 0.75em;"></span>'
    }

    onStart?.()

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this._csrfToken()
        }
      })

      if (!response.ok && response.status !== 202) {
        throw new Error(`Server error ${response.status}`)
      }
    } catch (error) {
      console.error('[financial-package-insights] Analysis request failed:', error)
      if (buttonTarget) {
        buttonTarget.disabled = false
        buttonTarget.innerHTML = originalButtonHtml
      }
      this.element.querySelector('[data-regenerating-placeholder]')?.remove()
      onError?.(banner, hasApproved)
    }
  }

  // ── Private ───────────────────────────────────────────────────────────────

  async _updateInsight(card, newStatus, rejectionReason = null) {
    const insightId = card.dataset.insightId

    const body = { status: newStatus }
    if (rejectionReason) body.rejection_reason = rejectionReason

    try {
      const response = await fetch(this._insightUrl(insightId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this._csrfToken()
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) throw new Error(`Server error ${response.status}`)

      card.dataset.insightStatus = newStatus
      this._applyCardState(card, newStatus)
      this._hideRejectForm(card)
      this._notifyComposeInsightsChanged()
      this._refreshSummary()
    } catch (error) {
      console.error('[financial-package-insights] Failed to update insight:', error)
    }
  }

  _applyCardState(card, status) {
    const pendingActions  = card.querySelector('[data-financial-package-insights-target="pendingActions"]')
    const approvedActions = card.querySelector('[data-financial-package-insights-target="approvedActions"]')
    const rejectedActions = card.querySelector('[data-financial-package-insights-target="rejectedActions"]')
    const headline        = card.querySelector('[data-financial-package-insights-target="headline"]')
    const content         = card.querySelector('[data-financial-package-insights-target="content"]')

    // Reset card border / bg
    card.classList.remove('border-0', 'opacity-50', 'border-light', 'shadow-sm', 'bolt-card-pending', 'bolt-card-approved')

    if (status === 'approved') {
      card.classList.add('bolt-card-approved')
      pendingActions?.classList.add('d-none')
      approvedActions?.classList.remove('d-none')
      rejectedActions?.classList.add('d-none')
      headline?.classList.remove('text-decoration-line-through', 'text-muted')
      content?.classList.remove('text-decoration-line-through')
    } else if (status === 'rejected') {
      card.classList.add('border-0', 'opacity-50', 'bg-white')
      pendingActions?.classList.add('d-none')
      approvedActions?.classList.add('d-none')
      rejectedActions?.classList.remove('d-none')
      headline?.classList.add('text-decoration-line-through', 'text-muted')
      content?.classList.add('text-decoration-line-through')
    } else {
      // pending
      card.classList.add('bolt-card-pending', 'shadow-sm')
      pendingActions?.classList.remove('d-none')
      approvedActions?.classList.add('d-none')
      rejectedActions?.classList.add('d-none')
      headline?.classList.remove('text-decoration-line-through', 'text-muted')
      content?.classList.remove('text-decoration-line-through')
    }
  }

  _prepareRegenerateWithApproved() {
    this.cardTargets
      .filter(c => c.dataset.insightStatus !== 'approved')
      .forEach(c => c.remove())

    this._insertRegeneratingSpinner()
    this._refreshSummary()
  }

  _showFullPanelSpinner(mode = 'regenerating') {
    const list = this.hasInsightsListTarget ? this.insightsListTarget : null
    if (list) {
      list.innerHTML = this._regeneratingSpinnerHtml(true, mode)
      return
    }

    const body = this.element.querySelector('.overflow-auto') || this.element
    const existing = body.querySelector('[data-regenerating-placeholder]')
    if (existing) return

    const el = document.createElement('div')
    el.dataset.regeneratingPlaceholder = ''
    el.innerHTML = this._regeneratingSpinnerHtml(true, mode)
    body.appendChild(el)
  }

  _insertRegeneratingSpinner() {
    if (this.element.querySelector('[data-regenerating-placeholder]')) return

    const el = document.createElement('div')
    el.dataset.regeneratingPlaceholder = ''
    el.className = 'd-flex align-items-center gap-2 px-3 py-2 border-bottom flex-shrink-0'
    el.style.background = '#fff6ef'
    el.innerHTML = this._regeneratingBarHtml()

    if (this.hasInsightsListTarget) {
      this.insightsListTarget.before(el)
    } else {
      this.element.prepend(el)
    }
  }

  _regeneratingBarHtml() {
    return `
      <span class="spinner-border spinner-border-sm text-warning flex-shrink-0" role="status" style="width: 1rem; height: 1rem; border-width: 2px;">
        <span class="visually-hidden">Loading…</span>
      </span>
      <div class="lh-sm">
        <div class="fw-semibold" style="font-size: 0.85em; color: #c95200;">Generating additional insights…</div>
        <div class="text-muted" style="font-size: 0.75em;">Approved insights stay in your email.</div>
      </div>`
  }

  _regeneratingSpinnerHtml(fullPanel, mode = 'regenerating') {
    const analyzing = mode === 'analyzing'
    const title = analyzing
      ? 'BOLT is analyzing the package…'
      : (fullPanel ? 'BOLT is generating insights…' : 'Generating additional insights…')
    const subtitle = analyzing
      ? 'Insights will appear here once analysis is complete.'
      : (fullPanel
        ? 'Insights will appear here once generation is complete.'
        : 'Approved insights stay in your email.')

    return `
      <div class="text-center py-${fullPanel ? '5' : '4'} px-3 text-muted d-flex flex-column align-items-center justify-content-center gap-3">
        <div class="spinner-border text-warning" role="status" style="width: 2rem; height: 2rem;">
          <span class="visually-hidden">Loading…</span>
        </div>
        <div>
          <p class="fw-semibold mb-1" style="font-size: ${fullPanel ? '0.9em' : '0.85em'}; color: #c95200;">${title}</p>
          <p class="mb-0 small">${subtitle}</p>
        </div>
      </div>`
  }

  _notifyComposeInsightsChanged() {
    document.dispatchEvent(new CustomEvent('bolt-insights:update'))
  }

  _refreshSummary() {
    const cards         = this.cardTargets
    const approvedCount = cards.filter(c => c.dataset.insightStatus === 'approved').length
    const pendingCount  = cards.filter(c => c.dataset.insightStatus === 'pending').length
    const total         = cards.length

    if (!this.hasSummaryTextTarget) return

    let html = `${approvedCount} of ${total} added to email`
    if (pendingCount > 0) {
      html += ` · <span class="text-warning fw-semibold">${pendingCount} pending</span>`
    }
    this.summaryTextTarget.innerHTML = html
  }

  _hideRejectForm(card) {
    const form  = card.querySelector('[data-financial-package-insights-target="rejectForm"]')
    const input = card.querySelector('[data-financial-package-insights-target="rejectReasonInput"]')
    const chips = card.querySelectorAll('.reject-chip')

    form?.classList.add('d-none')
    if (input) input.value = ''
    chips.forEach(c => {
      c.dataset.selected  = 'false'
      c.style.background  = 'var(--bolt-chip-bg)'
      c.style.color       = 'var(--bolt-chip-color)'
      c.style.borderColor = 'var(--bolt-chip-border)'
    })
  }

  _cardFor(event) {
    return event.currentTarget.closest('[data-insight-id]')
  }

  _insightUrl(insightId) {
    return `/tasks/${this.taskIdValue}/financial_package_insights/${insightId}`
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  }
}
